import { inject, Injectable } from '@angular/core';
import {
  Attachment,
  AttachmentEntityType,
  ResolvedAttachment,
} from '../models/attachment.model';
import { AuthService } from './auth.service';
import { BlobStorageService } from './blob-storage.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Photos (and later documents) attached to interactions, addresses, and
 * leads. Binary payloads go through BlobStorageService into the
 * content-addressed `blobs` table; each attachment row holds only the
 * `blob:sha256:` reference.
 */
@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);
  private blobStorage = inject(BlobStorageService);
  private logger = inject(LoggerService).getLogger('attachment');

  /**
   * Store each data URL as a blob and link it to the entity, preserving the
   * given order. Returns the attachment rows created.
   */
  async saveFor(
    entityType: AttachmentEntityType,
    entityId: string,
    dataUrls: string[],
  ): Promise<Attachment[]> {
    if (dataUrls.length === 0) return [];
    const userId = this.authService.getCurrentUserId();
    if (!userId) throw new Error('User must be authenticated to attach files');

    await this.db.init();
    const { photos } = await this.blobStorage.extractBinaryToBlobs({ photos: dataUrls });
    const refs = photos as string[];
    const now = this.clockSync.now();
    const existingCount = (await this.getFor(entityType, entityId)).length;
    const created: Attachment[] = [];

    for (let i = 0; i < refs.length; i++) {
      const id = this.db.generateId();
      const mimeType = this.mimeTypeOf(dataUrls[i]);
      await this.db.run(
        `INSERT INTO attachments (id, entity_type, entity_id, blob_ref, mime_type, caption, sort_order,
           created_by, created_at, updated_by, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, entityType, entityId, refs[i], mimeType, '', existingCount + i, userId, now, userId, now],
      );
      created.push({
        id,
        entity_type: entityType,
        entity_id: entityId,
        blob_ref: refs[i],
        mime_type: mimeType,
        caption: '',
        sort_order: existingCount + i,
        created_by: userId,
        created_at: now,
        updated_by: userId,
        updated_at: now,
        deleted_at: null,
      });
    }

    this.logger.debug('Saved attachments:', { entityType, entityId, count: created.length });
    return created;
  }

  /** Attachment rows for one entity, in display order (refs unresolved). */
  async getFor(entityType: AttachmentEntityType, entityId: string): Promise<Attachment[]> {
    await this.db.init();
    const rows = await this.db.exec(
      `SELECT * FROM attachments WHERE entity_type = ? AND entity_id = ? AND deleted_at IS NULL
       ORDER BY sort_order, created_at`,
      [entityType, entityId],
    );
    return rows as Attachment[];
  }

  /** Same as {@link getFor} but with each blob resolved to a data URL for display. */
  async getResolvedFor(
    entityType: AttachmentEntityType,
    entityId: string,
  ): Promise<ResolvedAttachment[]> {
    const rows = await this.getFor(entityType, entityId);
    if (rows.length === 0) return [];
    const { urls } = await this.blobStorage.resolveBlobReferences({
      urls: rows.map((r) => r.blob_ref),
    });
    return rows.map((row, i) => ({ ...row, data_url: (urls as string[])[i] ?? '' }));
  }

  /**
   * Resolved attachments for many entities at once, keyed by entity id —
   * for timelines that render photos on every row.
   */
  async getResolvedForMany(
    entityType: AttachmentEntityType,
    entityIds: string[],
  ): Promise<Map<string, ResolvedAttachment[]>> {
    const result = new Map<string, ResolvedAttachment[]>();
    if (entityIds.length === 0) return result;

    await this.db.init();
    const placeholders = entityIds.map(() => '?').join(', ');
    const rows = (await this.db.exec(
      `SELECT * FROM attachments WHERE entity_type = ? AND entity_id IN (${placeholders}) AND deleted_at IS NULL
       ORDER BY sort_order, created_at`,
      [entityType, ...entityIds],
    )) as Attachment[];
    if (rows.length === 0) return result;

    const { urls } = await this.blobStorage.resolveBlobReferences({
      urls: rows.map((r) => r.blob_ref),
    });
    rows.forEach((row, i) => {
      const list = result.get(row.entity_id) ?? [];
      list.push({ ...row, data_url: (urls as string[])[i] ?? '' });
      result.set(row.entity_id, list);
    });
    return result;
  }

  createResolvedQueryFor(
    entityType: AttachmentEntityType,
    entityId: string,
  ): QuerySignal<ResolvedAttachment[]> {
    return this.db.createQuery('attachments', () => this.getResolvedFor(entityType, entityId));
  }

  /** Soft-delete one attachment. Blob ref-counting is left to the blob layer. */
  async delete(id: string): Promise<void> {
    const userId = this.authService.getCurrentUserId();
    if (!userId) throw new Error('User must be authenticated to delete an attachment');
    await this.db.init();
    const now = this.clockSync.now();
    await this.db.run(
      'UPDATE attachments SET deleted_at = ?, updated_by = ?, updated_at = ? WHERE id = ?',
      [now, userId, now, id],
    );
  }

  private mimeTypeOf(dataUrl: string): string {
    const match = dataUrl.match(/^data:([^;]+)/);
    return match ? match[1] : 'application/octet-stream';
  }
}
